/* ==========================================================================
   CRAFT MOTORSPORT — v5 interaction layer
   Loader · cursor · reveal · split text · magnetic · tilt · counters ·
   parallax · marquee · rails · PDP gallery/variants/ATC · PLP filters ·
   hover followers · toast. Vanilla, module, no deps.
   ========================================================================== */

const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const finePointer = window.matchMedia('(pointer: fine)').matches;
const lerp = (a, b, n) => (1 - n) * a + n * b;
const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const $ = (s, c = document) => c.querySelector(s);
const $$ = (s, c = document) => [...c.querySelectorAll(s)];

/* --------------------------------------------------------------------------
   Money
   -------------------------------------------------------------------------- */
function money(cents) {
  const fmt = window.Theme?.moneyFormat || window.theme?.moneyFormat || '${{amount}}';
  const n = (cents / 100).toFixed(2);
  const [whole, dec] = n.split('.');
  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return fmt
    .replace(/\{\{\s*amount\s*\}\}/, `${withCommas}.${dec}`)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${whole}.${dec}`.replace('.', ','))
    .replace(/<[^>]+>/g, '');
}

/* --------------------------------------------------------------------------
   Toast
   -------------------------------------------------------------------------- */
let toastEl;
let toastTimer;
function toast(html, img) {
  if (!toastEl) {
    toastEl = document.createElement('div');
    toastEl.className = 'v5-toast';
    toastEl.setAttribute('role', 'status');
    document.body.appendChild(toastEl);
  }
  toastEl.innerHTML = `${
    img
      ? `<span class="v5-plate"><img src="${img}" alt=""></span>`
      : '<i><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 8.5l3 3 7-7"/></svg></i>'
  }<span>${html}</span>${window.Theme?.routes?.cart_url || window.V5_PREVIEW ? `<a href="${window.Theme?.routes?.cart_url || '#cart'}">View cart</a>` : ''}`;
  requestAnimationFrame(() => toastEl.classList.add('is-on'));
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toastEl.classList.remove('is-on'), 3600);
}

/* --------------------------------------------------------------------------
   Loader
   -------------------------------------------------------------------------- */
function initLoader() {
  const loader = $('.v5-loader');
  if (!loader) return;
  let seen = false;
  try {
    seen = !!sessionStorage.getItem('v5:seen');
  } catch (e) {}
  const count = $('.v5-loader__count b', loader);
  const bar = $('.v5-loader__bar i', loader);
  const duration = seen ? 650 : prefersReduced ? 300 : 1900;
  const start = performance.now();
  document.body.classList.add('v5-lock');

  function tick(now) {
    const p = clamp((now - start) / duration, 0, 1);
    const eased = 1 - Math.pow(1 - p, 3);
    if (count) count.textContent = String(Math.round(eased * 100)).padStart(3, '0');
    if (bar) bar.style.setProperty('--p', eased.toFixed(3));
    if (p < 1) requestAnimationFrame(tick);
    else finish();
  }
  function finish() {
    loader.classList.add('is-done');
    document.body.classList.remove('v5-lock');
    document.body.classList.add('v5-page-enter');
    try {
      sessionStorage.setItem('v5:seen', '1');
    } catch (e) {}
    setTimeout(() => {
      loader.hidden = true;
      document.dispatchEvent(new CustomEvent('v5:ready'));
    }, 1000);
  }
  requestAnimationFrame(tick);
}

/* --------------------------------------------------------------------------
   Cursor
   -------------------------------------------------------------------------- */
function initCursor() {
  const cursor = $('.v5-cursor');
  if (!cursor || !finePointer || prefersReduced) return;
  document.body.classList.add('v5-has-cursor');
  const dot = $('.v5-cursor__dot', cursor);
  const ring = $('.v5-cursor__ring', cursor);
  const label = $('.v5-cursor__label', cursor);
  let mx = innerWidth / 2,
    my = innerHeight / 2,
    rx = mx,
    ry = my;

  window.addEventListener(
    'pointermove',
    (e) => {
      mx = e.clientX;
      my = e.clientY;
      cursor.classList.remove('is-hidden');
    },
    { passive: true }
  );
  document.addEventListener('pointerleave', () => cursor.classList.add('is-hidden'));
  document.addEventListener('pointerdown', () => cursor.classList.add('is-down'));
  document.addEventListener('pointerup', () => cursor.classList.remove('is-down'));

  const interactive = 'a, button, [role=button], summary, label, select, .v5-card, .v5-chip';
  document.addEventListener('pointerover', (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    const labelled = t.closest('[data-cursor]');
    if (labelled) {
      cursor.classList.add('is-label');
      cursor.classList.remove('is-link', 'is-text');
      label.textContent = labelled.dataset.cursor;
      return;
    }
    if (t.closest('input[type=text], input[type=search], input[type=email], input[type=tel], input[type=number], textarea')) {
      cursor.classList.add('is-text');
      cursor.classList.remove('is-link', 'is-label');
      return;
    }
    if (t.closest(interactive)) {
      cursor.classList.add('is-link');
      cursor.classList.remove('is-label', 'is-text');
      return;
    }
    cursor.classList.remove('is-link', 'is-label', 'is-text');
  });

  (function loop() {
    rx = lerp(rx, mx, 0.18);
    ry = lerp(ry, my, 0.18);
    dot.style.transform = `translate(${mx}px, ${my}px)`;
    ring.style.transform = `translate(${rx}px, ${ry}px)`;
    requestAnimationFrame(loop);
  })();
}

/* --------------------------------------------------------------------------
   Reveal / split / lines / counters / manifesto
   -------------------------------------------------------------------------- */
function splitWords(el) {
  if (el.dataset.splitDone) return;
  el.dataset.splitDone = '1';
  const walk = (node) => {
    [...node.childNodes].forEach((child) => {
      if (child.nodeType === 3) {
        const words = child.textContent.split(/(\s+)/);
        const frag = document.createDocumentFragment();
        words.forEach((w) => {
          if (!w) return;
          if (/^\s+$/.test(w)) {
            frag.appendChild(document.createTextNode(' '));
            return;
          }
          const wrap = document.createElement('span');
          wrap.className = 'v5-w';
          const inner = document.createElement('span');
          inner.textContent = w;
          wrap.appendChild(inner);
          frag.appendChild(wrap);
        });
        node.replaceChild(frag, child);
      } else if (child.nodeType === 1 && child.tagName !== 'BR') {
        if (child.classList.contains('v5-accent') || child.classList.contains('v5-outline') || child.classList.contains('v5-blue')) {
          walk(child);
          $$('.v5-w', child).forEach((w) => w.classList.add(...[...child.classList]));
        } else walk(child);
      }
    });
  };
  walk(el);
  $$('.v5-w', el).forEach((w, i) => w.style.setProperty('--i', i));
  el.classList.add('v5-split');
}

function initReveal() {
  $$('[data-split]').forEach(splitWords);
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        en.target.classList.add('is-in');
        if (en.target.hasAttribute('data-count')) runCounter(en.target);
        io.unobserve(en.target);
      });
    },
    { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
  );
  $$('[data-reveal], [data-split], .v5-line-grow, [data-count]').forEach((el) => io.observe(el));

  // Manifesto: light words progressively as they pass the viewport centre
  const manifestos = $$('.v5-manifesto__text');
  if (manifestos.length) {
    const update = () => {
      manifestos.forEach((m) => {
        const words = $$('.v5-w', m);
        const r = m.getBoundingClientRect();
        const progress = clamp((innerHeight * 0.85 - r.top) / (r.height + innerHeight * 0.35), 0, 1);
        const lit = Math.floor(progress * words.length);
        words.forEach((w, i) => w.classList.toggle('is-lit', i < lit));
      });
    };
    window.addEventListener('scroll', update, { passive: true });
    update();
  }
}

function runCounter(el) {
  const target = parseFloat(el.dataset.count);
  const dec = (el.dataset.count.split('.')[1] || '').length;
  const dur = 1600;
  const start = performance.now();
  const step = (now) => {
    const p = clamp((now - start) / dur, 0, 1);
    const eased = 1 - Math.pow(1 - p, 4);
    el.textContent = (eased * target).toFixed(dec).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (p < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

/* --------------------------------------------------------------------------
   Magnetic buttons + tilt cards + parallax
   -------------------------------------------------------------------------- */
function initMagnetic() {
  if (!finePointer || prefersReduced) return;
  $$('.v5-btn, .v5-rail__btn, .v5-cat__arrow, .v5-coll-row__arrow').forEach((btn) => {
    const strength = btn.classList.contains('v5-btn') ? 0.25 : 0.4;
    btn.addEventListener('pointermove', (e) => {
      const r = btn.getBoundingClientRect();
      const x = e.clientX - r.left - r.width / 2;
      const y = e.clientY - r.top - r.height / 2;
      btn.style.transform = `translate(${x * strength}px, ${y * strength}px)`;
    });
    btn.addEventListener('pointerleave', () => {
      btn.style.transform = '';
    });
  });
}
function initTilt() {
  if (!finePointer || prefersReduced) return;
  $$('[data-tilt]').forEach((el) => {
    el.addEventListener('pointermove', (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      el.style.transform = `perspective(900px) rotateX(${-y * 6}deg) rotateY(${x * 8}deg) translateY(-4px)`;
    });
    el.addEventListener('pointerleave', () => {
      el.style.transform = '';
    });
  });
}
function initParallax() {
  if (prefersReduced) return;
  const items = $$('[data-parallax]');
  if (!items.length) return;
  const update = () => {
    items.forEach((el) => {
      const speed = parseFloat(el.dataset.parallax) || 0.2;
      const r = el.getBoundingClientRect();
      if (r.bottom < 0 || r.top > innerHeight) return;
      const offset = (r.top + r.height / 2 - innerHeight / 2) * speed;
      el.style.transform = `translate3d(0, ${offset}px, 0)`;
    });
  };
  window.addEventListener('scroll', update, { passive: true });
  update();
}

/* --------------------------------------------------------------------------
   Marquee with scroll velocity
   -------------------------------------------------------------------------- */
function initMarquee() {
  $$('.v5-marquee').forEach((m) => {
    const track = $('.v5-marquee__track', m);
    if (!track) return;
    const group = $('.v5-marquee__group', track);
    if (!group) return;
    if (m.dataset.v5Init) return;
    m.dataset.v5Init = '1';
    // Ensure enough copies to cover the viewport twice (guarded: a hidden marquee measures 0)
    let guard = 0;
    while (group.offsetWidth > 0 && track.scrollWidth < innerWidth * 2 + group.offsetWidth && guard++ < 12) {
      track.appendChild(group.cloneNode(true));
    }
    if (group.offsetWidth === 0) {
      for (let i = 0; i < 3; i++) track.appendChild(group.cloneNode(true));
    }
    const dir = m.dataset.direction === 'right' ? 1 : -1;
    const base = parseFloat(m.dataset.speed) || 0.6;
    let x = 0,
      lastY = scrollY,
      vel = 0;
    const w = () => group.offsetWidth;
    const loop = () => {
      const dy = scrollY - lastY;
      lastY = scrollY;
      vel = lerp(vel, clamp(dy * 0.35, -14, 14), 0.1);
      x += dir * (base + Math.abs(vel) * (prefersReduced ? 0 : 1)) * (prefersReduced ? 0.3 : 1);
      const width = w();
      if (width > 0) {
        if (dir < 0 && -x >= width) x = -((-x) % width);
        if (dir > 0 && x >= 0) x = (x % width) - width;
      }
      track.style.transform = `translate3d(${x}px,0,0)`;
      requestAnimationFrame(loop);
    };
    if (dir > 0) x = -w();
    requestAnimationFrame(loop);
  });
}

/* --------------------------------------------------------------------------
   Header scroll state
   -------------------------------------------------------------------------- */
function initHeaderState() {
  const update = () => document.body.classList.toggle('v5-scrolled', scrollY > 24);
  window.addEventListener('scroll', update, { passive: true });
  update();
}

/* --------------------------------------------------------------------------
   Rails: drag to scroll + arrows + progress
   -------------------------------------------------------------------------- */
function initRails() {
  $$('.v5-rail').forEach((rail) => {
    const track = $('.v5-rail__track', rail);
    if (!track) return;
    const prev = $('[data-rail-prev]', rail);
    const next = $('[data-rail-next]', rail);
    const prog = $('.v5-rail__progress i', rail);
    const step = () => (track.firstElementChild?.getBoundingClientRect().width || 300) + 8;
    prev?.addEventListener('click', () => track.scrollBy({ left: -step() * 2, behavior: 'smooth' }));
    next?.addEventListener('click', () => track.scrollBy({ left: step() * 2, behavior: 'smooth' }));
    const update = () => {
      if (!prog) return;
      const max = track.scrollWidth - track.clientWidth;
      const visible = track.clientWidth / track.scrollWidth;
      const p = max > 0 ? track.scrollLeft / max : 0;
      prog.style.setProperty('--p', Math.max(visible, 0.08).toFixed(3));
      prog.style.transformOrigin = `${(p * 100).toFixed(1)}% 50%`;
      prog.style.left = `${(p * (1 - Math.max(visible, 0.08)) * 100).toFixed(2)}%`;
      prog.style.right = 'auto';
      prog.style.width = `${(Math.max(visible, 0.08) * 100).toFixed(2)}%`;
      prog.style.transform = 'none';
    };
    track.addEventListener('scroll', update, { passive: true });
    update();

    // drag
    let down = false,
      startX = 0,
      startLeft = 0,
      moved = false;
    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse') return;
      down = true;
      moved = false;
      startX = e.clientX;
      startLeft = track.scrollLeft;
    });
    window.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 4) {
        moved = true;
        track.classList.add('is-dragging');
      }
      track.scrollLeft = startLeft - dx;
    });
    window.addEventListener('pointerup', () => {
      if (!down) return;
      down = false;
      setTimeout(() => track.classList.remove('is-dragging'), 50);
    });
    track.addEventListener(
      'click',
      (e) => {
        if (moved) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );
  });
}

/* --------------------------------------------------------------------------
   Cart helpers (talks to Horizon's cart drawer via its own events)
   -------------------------------------------------------------------------- */
async function cartAdd(formData, sourceId) {
  if (window.V5_PREVIEW) {
    // Static preview mode (no Shopify backend): simulate latency + success
    await new Promise((r) => setTimeout(r, 650));
    window.V5_PREVIEW.count = (window.V5_PREVIEW.count || 0) + (Number(formData.get('quantity')) || 1);
    $$('[data-cart-count]').forEach((el) => (el.textContent = window.V5_PREVIEW.count));
    const title = document.querySelector(`[data-quick-add="${formData.get('id')}"]`)?.closest('.v5-card')?.querySelector('.v5-card__title')?.textContent?.trim();
    return { line: { product_title: title || window.V5_PREVIEW.title || 'Part', image: null }, cart: { item_count: window.V5_PREVIEW.count } };
  }
  const sectionIds = $$('cart-items-component')
    .map((c) => c.dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));
  const root = window.Theme?.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add`;
  const res = await fetch(root, {
    method: 'POST',
    headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
    body: formData,
  });
  const json = await res.json();
  if (json.status) throw new Error(json.description || json.message || 'Could not add to cart');
  const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
  try {
    const { CartAddEvent } = await import('@theme/events');
    document.dispatchEvent(
      new CartAddEvent(cart, sourceId, {
        source: 'product-form-component',
        itemCount: Number(formData.get('quantity')) || 1,
        productId: String(json.product_id),
        variantId: String(json.variant_id),
        sections: json.sections,
      })
    );
  } catch (e) {
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { resource: cart } }));
  }
  // Refresh any header cart counts the drawer might not own
  $$('[data-cart-count]').forEach((el) => (el.textContent = cart.item_count));
  return { line: json, cart };
}

/* --------------------------------------------------------------------------
   Quick add from cards
   -------------------------------------------------------------------------- */
function initQuickAdd() {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    const variantId = btn.dataset.quickAdd;
    if (!variantId) return;
    btn.classList.add('is-loading');
    const fd = new FormData();
    fd.append('id', variantId);
    fd.append('quantity', '1');
    try {
      const { line } = await cartAdd(fd, btn.closest('.v5-card')?.id || 'quick-add');
      toast(`Added <b>${line.product_title}</b>`, line.image ? line.image.replace(/(\.[a-z]+)(\?|$)/, '_80x$1$2') : null);
    } catch (err) {
      toast(err.message || 'Could not add to cart');
    } finally {
      btn.classList.remove('is-loading');
    }
  });
}

/* --------------------------------------------------------------------------
   Product page
   -------------------------------------------------------------------------- */
function initPDP() {
  const pdp = $('.v5-pdp');
  if (!pdp) return;
  const dataEl = $('[data-v5-product]', pdp);
  const product = dataEl ? JSON.parse(dataEl.textContent) : null;
  if (!product) return;

  /* Gallery */
  const stage = $('.v5-gallery__stage', pdp);
  const slides = $$('.v5-gallery__slide', pdp);
  const thumbs = $$('.v5-gallery__thumb', pdp);
  const countEl = $('.v5-gallery__count', pdp);
  let dots = $('.v5-gallery__dots', pdp);
  if (!dots && stage && slides.length > 1) {
    dots = document.createElement('div');
    dots.className = 'v5-gallery__dots';
    dots.innerHTML = slides.map(() => '<i></i>').join('');
    stage.appendChild(dots);
  }
  let idx = Math.max(0, slides.findIndex((s) => s.classList.contains('is-active')));
  const show = (i) => {
    idx = (i + slides.length) % slides.length;
    slides.forEach((s, n) => s.classList.toggle('is-active', n === idx));
    thumbs.forEach((t, n) => t.classList.toggle('is-active', n === idx));
    if (dots) $$('i', dots).forEach((d, n) => d.classList.toggle('is-active', n === idx));
    thumbs[idx]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    if (countEl) countEl.textContent = `${String(idx + 1).padStart(2, '0')} / ${String(slides.length).padStart(2, '0')}`;
    stage?.classList.remove('is-zoom');
  };
  thumbs.forEach((t, i) => t.addEventListener('click', () => show(i)));
  $('[data-gallery-prev]', pdp)?.addEventListener('click', () => show(idx - 1));
  $('[data-gallery-next]', pdp)?.addEventListener('click', () => show(idx + 1));
  if (stage) {
    stage.addEventListener('click', (e) => {
      const img = e.target.closest('img');
      if (!img) return;
      stage.classList.toggle('is-zoom');
    });
    stage.addEventListener('pointermove', (e) => {
      if (!stage.classList.contains('is-zoom')) return;
      const img = $('.v5-gallery__slide.is-active img', stage);
      if (!img) return;
      const r = stage.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width) * 100;
      const y = ((e.clientY - r.top) / r.height) * 100;
      img.style.transformOrigin = `${x}% ${y}%`;
    });
    stage.addEventListener('pointerleave', () => stage.classList.remove('is-zoom'));
    // touch swipe
    let sx = 0;
    stage.addEventListener('touchstart', (e) => (sx = e.touches[0].clientX), { passive: true });
    stage.addEventListener(
      'touchend',
      (e) => {
        const dx = e.changedTouches[0].clientX - sx;
        if (Math.abs(dx) > 40) show(idx + (dx < 0 ? 1 : -1));
      },
      { passive: true }
    );
  }
  document.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft') show(idx - 1);
    if (e.key === 'ArrowRight') show(idx + 1);
  });

  /* Variants */
  const form = $('form[data-v5-form]', pdp);
  const idInput = $('input[name=id]', form);
  const priceEl = $('[data-v5-price]', pdp);
  const compareEl = $('[data-v5-compare]', pdp);
  const skuEl = $('[data-v5-sku]', pdp);
  const stockEl = $('[data-v5-stock]', pdp);
  const atc = $('.v5-atc', form);
  const atcText = $('.v5-atc__text', atc);
  const atcPrice = $('.v5-atc__price', atc);
  const optionInputs = $$('.v5-opt input[type=radio]', form);
  const stickyPrice = $('.v5-sticky-atc [data-v5-price]');

  const selectedOptions = () =>
    $$('.v5-opt', form).map((group) => $('input:checked', group)?.value);

  function findVariant() {
    const opts = selectedOptions();
    return product.variants.find((v) => v.options.every((o, i) => o === opts[i]));
  }

  function markAvailability() {
    const opts = selectedOptions();
    $$('.v5-opt', form).forEach((group, gi) => {
      $$('input', group).forEach((input) => {
        const test = [...opts];
        test[gi] = input.value;
        const match = product.variants.find((v) => v.options.every((o, i) => o === test[i]));
        input.nextElementSibling?.classList.toggle('is-unavailable', !match || !match.available);
      });
      const out = $('output', group);
      if (out) out.textContent = opts[gi] || '';
    });
  }

  function updateVariant(push = true) {
    const v = findVariant();
    markAvailability();
    if (!v) {
      atc.disabled = true;
      atcText.textContent = 'Unavailable';
      return;
    }
    idInput.value = v.id;
    if (priceEl) priceEl.textContent = money(v.price);
    if (stickyPrice) stickyPrice.textContent = money(v.price);
    if (compareEl) {
      if (v.compare_at_price && v.compare_at_price > v.price) {
        compareEl.textContent = money(v.compare_at_price);
        compareEl.hidden = false;
        priceEl?.parentElement?.classList.add('is-sale');
      } else {
        compareEl.hidden = true;
        priceEl?.parentElement?.classList.remove('is-sale');
      }
    }
    if (skuEl) skuEl.textContent = v.sku ? `SKU ${v.sku}` : '';
    if (stockEl) {
      stockEl.className = 'v5-stock';
      if (!v.available) {
        stockEl.classList.add('v5-stock--out');
        stockEl.textContent = 'Sold out';
      } else if (v.inventory_management && v.inventory_quantity > 0 && v.inventory_quantity <= 5) {
        stockEl.classList.add('v5-stock--low');
        stockEl.textContent = `Only ${v.inventory_quantity} left`;
      } else if (v.inventory_management && v.inventory_quantity <= 0 && v.available) {
        stockEl.textContent = 'Ships on backorder';
      } else {
        stockEl.textContent = 'In stock · ships fast';
      }
    }
    atc.disabled = !v.available;
    atcText.textContent = v.available ? atc.dataset.label || 'Add to cart' : 'Sold out';
    if (atcPrice) atcPrice.textContent = money(v.price * (parseInt($('input[name=quantity]', form)?.value) || 1));
    // swap gallery to variant image
    if (v.featured_media) {
      const i = slides.findIndex((s) => s.dataset.mediaId === String(v.featured_media.id));
      if (i >= 0) show(i);
    }
    if (push) {
      const url = new URL(location.href);
      url.searchParams.set('variant', v.id);
      history.replaceState({}, '', url);
    }
    document.dispatchEvent(new CustomEvent('v5:variant', { detail: { variant: v } }));
  }
  optionInputs.forEach((i) => i.addEventListener('change', () => updateVariant()));
  if (optionInputs.length) updateVariant(false);
  else if (atcPrice && product.variants[0]) atcPrice.textContent = money(product.variants[0].price);

  /* Quantity */
  const qty = $('input[name=quantity]', form);
  $$('[data-qty]', form).forEach((b) =>
    b.addEventListener('click', () => {
      const n = clamp((parseInt(qty.value) || 1) + (b.dataset.qty === '+' ? 1 : -1), 1, 999);
      qty.value = n;
      const v = findVariant() || product.variants[0];
      if (atcPrice && v) atcPrice.textContent = money(v.price * n);
    })
  );

  /* Add to cart */
  form?.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (atc.disabled) return;
    atc.classList.add('is-loading');
    try {
      const { line } = await cartAdd(new FormData(form), form.id);
      atc.classList.remove('is-loading');
      atc.classList.add('is-added');
      atcText.textContent = 'Added';
      toast(`<b>${line.product_title}</b> is in your cart`, line.image ? line.image.replace(/(\.[a-z]+)(\?|$)/, '_80x$1$2') : null);
      setTimeout(() => {
        atc.classList.remove('is-added');
        atcText.textContent = atc.dataset.label || 'Add to cart';
      }, 2200);
    } catch (err) {
      atc.classList.remove('is-loading');
      toast(err.message);
    }
  });

  /* Sticky ATC */
  const sticky = $('.v5-sticky-atc');
  if (sticky && form) {
    const io = new IntersectionObserver(([en]) => sticky.classList.toggle('is-on', !en.isIntersecting && en.boundingClientRect.top < 0), {
      threshold: 0,
    });
    io.observe(form);
    $('button', sticky)?.addEventListener('click', () => form.requestSubmit());
  }

  /* Recommendations */
  const recs = $('[data-v5-recs]');
  if (recs) {
    const url = `${window.Shopify?.routes?.root || '/'}recommendations/products?section_id=${recs.dataset.sectionId}&product_id=${product.id}&limit=8&intent=related`;
    fetch(url)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = doc.querySelector('[data-v5-recs-inner]');
        if (inner && inner.children.length) {
          recs.querySelector('[data-v5-recs-inner]').replaceWith(inner);
          recs.hidden = false;
          initRails();
          initReveal();
          initPlates();
        } else recs.remove();
      })
      .catch(() => recs.remove());
  }
}

/* --------------------------------------------------------------------------
   Collection page: filters auto-submit, drawer, view toggle
   -------------------------------------------------------------------------- */
function initPLP() {
  const plp = $('.v5-plp');
  if (!plp) return;
  const form = $('form[data-v5-filters]', plp);
  const filters = $('.v5-filters', plp);
  const layout = $('.v5-plp__layout', plp);
  const scrim = $('.v5-scrim') || document.body.appendChild(Object.assign(document.createElement('div'), { className: 'v5-scrim' }));
  const grid = $('.v5-plp__grid', plp);

  const open = (on) => {
    filters?.classList.toggle('is-open', on);
    scrim.classList.toggle('is-on', on);
    document.body.classList.toggle('v5-lock', on);
  };
  $$('[data-filters-toggle]', plp).forEach((b) =>
    b.addEventListener('click', () => {
      if (innerWidth < 990) open(!filters.classList.contains('is-open'));
      else {
        layout.classList.toggle('filters-hidden');
        b.classList.toggle('is-active', !layout.classList.contains('filters-hidden'));
        try { localStorage.setItem('v5:filters', layout.classList.contains('filters-hidden') ? 'hidden' : 'shown'); } catch (e) {}
      }
    })
  );
  try { if (localStorage.getItem('v5:filters') === 'hidden' && innerWidth >= 990) layout?.classList.add('filters-hidden'); } catch (e) {}
  $('[data-filters-close]', plp)?.addEventListener('click', () => open(false));
  scrim.addEventListener('click', () => open(false));

  let t;
  const submit = () => {
    clearTimeout(t);
    t = setTimeout(() => {
      // reset pagination
      const url = new URL(form.action, location.origin);
      const fd = new FormData(form);
      const params = new URLSearchParams();
      for (const [k, v] of fd.entries()) if (v !== '') params.append(k, v);
      url.search = params.toString();
      $('.v5-plp__grid', plp)?.classList.add('is-loading');
      fetch(url.toString(), { headers: { Accept: 'text/html' } })
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newPlp = doc.querySelector('.v5-plp');
          if (!newPlp) {
            location.href = url.toString();
            return;
          }
          ['.v5-plp__grid', '.v5-filters__active', '.v5-plp__count', '.v5-pagination', '.v5-filters__inner'].forEach((sel) => {
            const a = $(sel, plp);
            const b = newPlp.querySelector(sel);
            if (a && b) a.replaceWith(b);
            else if (a && !b) a.innerHTML = '';
          });
          history.replaceState({}, '', url.toString());
          initReveal();
          bindFilterInputs();
          $('.v5-plp__grid', plp)?.classList.remove('is-loading');
          initPlates();
        })
        .catch(() => (location.href = url.toString()));
    }, 250);
  };
  function bindFilterInputs() {
    const f = $('form[data-v5-filters]', plp);
    if (!f) return;
    $$('input[type=checkbox], input[type=radio], select', f).forEach((i) => (i.onchange = submit));
    $$('input[type=number]', f).forEach((i) => (i.onchange = submit));
    $$('.v5-filter__search', f).forEach((s) => {
      s.oninput = () => {
        const q = s.value.toLowerCase();
        $$('.v5-check', s.parentElement).forEach((c) => (c.style.display = c.textContent.toLowerCase().includes(q) ? '' : 'none'));
      };
    });
    f.onsubmit = (e) => {
      e.preventDefault();
      submit();
    };
  }
  bindFilterInputs();
  $('[data-v5-sort]', plp)?.addEventListener('change', (e) => {
    const url = new URL(location.href);
    url.searchParams.set('sort_by', e.target.value);
    url.searchParams.delete('page');
    location.href = url.toString();
  });
  $$('[data-view]', plp).forEach((b) =>
    b.addEventListener('click', () => {
      $$('[data-view]', plp).forEach((x) => x.classList.toggle('is-active', x === b));
      $('.v5-plp__grid', plp)?.setAttribute('data-view', b.dataset.view);
      try { localStorage.setItem('v5:view', b.dataset.view); } catch (e) {}
    })
  );
  let savedView = null;
  try { savedView = localStorage.getItem('v5:view'); } catch (e) {}
  if (savedView) $(`[data-view="${savedView}"]`, plp)?.click();
}

/* --------------------------------------------------------------------------
   Hover image followers (services list, collections list)
   -------------------------------------------------------------------------- */
function initFollowers() {
  if (!finePointer) return;
  $$('[data-follow-img]').forEach((row) => {
    const src = row.dataset.followImg;
    if (!src) return;
    const box = document.createElement('div');
    box.className = row.classList.contains('v5-coll-row') ? 'v5-coll-row__img' : 'v5-svc__img';
    box.innerHTML = `<img src="${src}" alt="" loading="lazy">`;
    document.body.appendChild(box);
    let x = 0,
      y = 0,
      tx = 0,
      ty = 0,
      on = false;
    row.addEventListener('pointerenter', (e) => {
      on = true;
      x = tx = e.clientX;
      y = ty = e.clientY;
      box.classList.add('is-on');
      loop();
    });
    row.addEventListener('pointermove', (e) => {
      tx = e.clientX;
      ty = e.clientY;
    });
    row.addEventListener('pointerleave', () => {
      on = false;
      box.classList.remove('is-on');
    });
    function loop() {
      if (!on) return;
      x = lerp(x, tx, 0.15);
      y = lerp(y, ty, 0.15);
      box.style.transform = `translate(${x + 24}px, ${y - box.offsetHeight / 2}px) ${box.classList.contains('is-on') ? '' : ''}`;
      box.style.left = '0';
      box.style.top = '0';
      requestAnimationFrame(loop);
    }
  });
}

/* --------------------------------------------------------------------------
   Forms: select floating labels, pills → hidden field, article progress, clock
   -------------------------------------------------------------------------- */
function initMisc() {
  $$('.v5-field select').forEach((s) => {
    const upd = () => s.classList.toggle('has-value', !!s.value);
    s.addEventListener('change', upd);
    upd();
  });
  // Article reading progress
  const prog = $('.v5-article__progress');
  const content = $('.v5-article__content');
  if (prog && content) {
    const upd = () => {
      const r = content.getBoundingClientRect();
      const p = clamp((innerHeight * 0.5 - r.top) / r.height, 0, 1);
      prog.style.transform = `scaleX(${p})`;
    };
    window.addEventListener('scroll', upd, { passive: true });
    upd();
  }
  // Copy link
  $$('[data-copy-link]').forEach((b) =>
    b.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(location.href);
        toast('Link copied');
      } catch (e) {}
    })
  );
  // Footer open/closed clock (Eastern, Mon–Fri 9–5 default; configurable via data attrs)
  $$('.v5-footer__clock').forEach((c) => {
    const tz = c.dataset.tz || 'America/New_York';
    const openH = parseInt(c.dataset.open) || 9;
    const closeH = parseInt(c.dataset.close) || 17;
    const days = (c.dataset.days || '1,2,3,4,5').split(',').map(Number);
    const upd = () => {
      const now = new Date();
      const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', hour12: false, weekday: 'short', minute: '2-digit' }).formatToParts(now);
      const h = parseInt(parts.find((p) => p.type === 'hour').value) % 24;
      const m = parts.find((p) => p.type === 'minute').value;
      const wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(parts.find((p) => p.type === 'weekday').value);
      const isOpen = days.includes(wd) && h >= openH && h < closeH;
      c.classList.toggle('is-closed', !isOpen);
      c.textContent = `${isOpen ? 'Shop open' : 'Shop closed'} · ${String(h).padStart(2, '0')}:${m} ET`;
    };
    upd();
    setInterval(upd, 30000);
  });
  // Today highlight in hours table
  const today = new Date().getDay();
  $$('.v5-hours [data-day]').forEach((d) => d.classList.toggle('is-today', parseInt(d.dataset.day) === today));
}

/* --------------------------------------------------------------------------
   Media plates: detect dark-background product photos and flip their plate
   -------------------------------------------------------------------------- */
const plateCache = new Map();
function samplePlate(img) {
  const plate = img.closest('.v5-plate');
  if (!plate || plate.dataset.plateDone) return;
  const key = (img.currentSrc || img.src || '').split('?')[0];
  if (!key) return;
  const apply = (dark) => {
    plate.dataset.plateDone = '1';
    plate.classList.toggle('is-dark', dark);
  };
  if (plateCache.has(key)) return apply(plateCache.get(key));
  const probe = new Image();
  probe.crossOrigin = 'anonymous';
  probe.decoding = 'async';
  probe.onload = () => {
    try {
      const c = document.createElement('canvas');
      c.width = 24;
      c.height = 24;
      const ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(probe, 0, 0, 24, 24);
      const d = ctx.getImageData(0, 0, 24, 24).data;
      // sample a 2px frame around the edge
      let lum = 0,
        n = 0;
      for (let y = 0; y < 24; y++)
        for (let x = 0; x < 24; x++) {
          if (x > 1 && x < 22 && y > 1 && y < 22) continue;
          const i = (y * 24 + x) * 4;
          if (d[i + 3] < 40) {
            lum += 255; // transparent reads as white plate
          } else lum += 0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2];
          n++;
        }
      const avg = lum / n;
      const dark = avg < 110;
      plateCache.set(key, dark);
      apply(dark);
    } catch (e) {
      plateCache.set(key, false);
      apply(false);
    }
  };
  probe.onerror = () => apply(false);
  probe.src = img.currentSrc || img.src;
}
function initPlates() {
  $$('.v5-plate img').forEach((img) => {
    if (img.complete && img.naturalWidth) samplePlate(img);
    else img.addEventListener('load', () => samplePlate(img), { once: true });
  });
}

/* --------------------------------------------------------------------------
   Page wipe on internal navigation + back-to-top ring
   -------------------------------------------------------------------------- */
function initWipe() {
  if (prefersReduced) return;
  let wipe = $('.v5-wipe');
  if (!wipe) {
    wipe = document.createElement('div');
    wipe.className = 'v5-wipe';
    wipe.setAttribute('aria-hidden', 'true');
    wipe.innerHTML = [0, 1, 2, 3, 4].map((i) => `<i style="--i:${i}"></i>`).join('');
    document.body.appendChild(wipe);
  }
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href]');
    if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || a.target === '_blank') return;
    const href = a.getAttribute('href');
    if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript:')) return;
    let url;
    try {
      url = new URL(a.href, location.href);
    } catch (err) {
      return;
    }
    if (url.origin !== location.origin) return;
    if (url.pathname === location.pathname && url.search === location.search) return;
    if (a.closest('.v5-rail__track.is-dragging')) return;
    e.preventDefault();
    wipe.classList.add('is-on');
    setTimeout(() => (location.href = url.href), 520);
  });
  window.addEventListener('pageshow', () => wipe.classList.remove('is-on'));
}
function initBackToTop() {
  let btn = $('.v5-top');
  if (!btn) {
    btn = document.createElement('button');
    btn.className = 'v5-top';
    btn.type = 'button';
    btn.setAttribute('aria-label', 'Back to top');
    btn.innerHTML =
      '<svg class="ring" viewBox="0 0 52 52"><circle cx="26" cy="26" r="24"/><circle cx="26" cy="26" r="24"/></svg><svg class="arrow" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 13V3M3 8l5-5 5 5"/></svg>';
    document.body.appendChild(btn);
  }
  btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
  const upd = () => {
    const max = document.documentElement.scrollHeight - innerHeight;
    const p = max > 0 ? scrollY / max : 0;
    btn.style.setProperty('--p', p.toFixed(3));
    btn.classList.toggle('is-on', scrollY > innerHeight * 0.8);
  };
  window.addEventListener('scroll', upd, { passive: true });
  upd();
}

/* --------------------------------------------------------------------------
   Boot
   -------------------------------------------------------------------------- */
function boot() {
  initLoader();
  initCursor();
  initHeaderState();
  initReveal();
  initMagnetic();
  initTilt();
  initParallax();
  initMarquee();
  initRails();
  initQuickAdd();
  initPDP();
  initPLP();
  initFollowers();
  initMisc();
  initPlates();
  initWipe();
  initBackToTop();
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();

// Re-init when Shopify theme editor re-renders a section
document.addEventListener('shopify:section:load', () => {
  initReveal();
  initMagnetic();
  initMarquee();
  initRails();
  initFollowers();
  initMisc();
  initPlates();
});

window.WerksV5 = { toast, cartAdd, money };
